import Sqids from 'sqids';
import { FormAnswerType } from '@jlg-website/shared';
import type { AnswerValue, Answers, QuestionDataInput } from '@jlg-website/shared';

/**
 * URL transport for the answers a visitor gives in the mass-tort hero's first step, so the
 * full form at `/forms/<slug>` can pre-select them.
 *
 * The rule lives here (in the app), never in `@jlg-website/form`: the package only accepts a
 * neutral `initialAnswers` map. Answers are packed into a single Sqids token — short, reversible
 * and free of raw database ids in the URL — laid out as a flat integer array, one group per
 * answered question:
 *
 *   [questionId, count, ...values]
 *
 *   - boolean       → count 1, value 1 (true) or 0 (false)
 *   - single choice → count 1, value = option id
 *   - multi choice  → count N, values = option ids
 *
 * The question's *type* is not stored: it is looked up from `availableQuestions` on decode,
 * which also disambiguates a boolean 0/1 from an option id and rebuilds each answer's `score`
 * (never carried in the URL). Free-text answers can't be integer-packed and are skipped — the
 * hero's qualifying first step is always a choice/boolean question.
 */

// minLength keeps very short payloads from decoding to an obviously-numeric-looking token.
const sqids = new Sqids({ minLength: 6 });

const QUERY_KEY = 'a';

const isMultipleChoice = (type: string): boolean =>
  type === FormAnswerType.LABELED_MULTIPLE_OPTIONS ||
  type === FormAnswerType.DROPDOWN_MULTIPLE_OPTIONS;

/** Non-negative integers a single answer packs into, or null when it can't be encoded. */
function valueToInts(value: AnswerValue['value']): number[] | null {
  if (typeof value === 'boolean') return [value ? 1 : 0];

  const toInt = (v: string | number | boolean): number | null => {
    const n = Number(v);
    return Number.isInteger(n) && n >= 0 ? n : null;
  };

  if (Array.isArray(value)) {
    const ints = value.map(toInt);
    return ints.every((n): n is number => n !== null) ? ints : null;
  }

  if (value === null) return null;
  const n = toInt(value);
  return n === null ? null : [n];
}

/** Encodes a flat answer map into a Sqids token, or null when nothing encodable is present. */
export function encodeAnswers(answers: Answers): string | null {
  const nums: number[] = [];
  for (const [questionId, answer] of Object.entries(answers)) {
    const id = Number(questionId);
    if (!Number.isInteger(id) || id < 0) continue;
    const ints = valueToInts(answer.value);
    if (!ints || ints.length === 0) continue;
    nums.push(id, ints.length, ...ints);
  }
  return nums.length > 0 ? sqids.encode(nums) : null;
}

/** Recreates the score an answer would have carried, matching the renderer's `scoreOfAnswer`. */
function scoreForOption(question: QuestionDataInput | undefined, optionId: string): number {
  const option = question?.options?.find((opt) => String(opt.id) === optionId);
  return option?.score ?? 0;
}

function intsToAnswer(question: QuestionDataInput, ints: number[]): AnswerValue {
  if (question.type === FormAnswerType.BOOLEAN) {
    return { value: ints[0] === 1, score: [0] };
  }

  if (isMultipleChoice(question.type)) {
    const value = ints.map(String);
    return { value, score: value.map((id) => scoreForOption(question, id)) };
  }

  const value = String(ints[0]);
  return { value, score: [scoreForOption(question, value)] };
}

/** Decodes a Sqids token back into an `Answers` map, using the question catalog for typing. */
export function decodeAnswers(
  token: string,
  availableQuestions: QuestionDataInput[] = []
): Answers {
  let nums: number[];
  try {
    nums = sqids.decode(token);
  } catch {
    return {};
  }

  const byId = new Map(availableQuestions.map((q) => [q.question_id, q]));
  const answers: Answers = {};

  let i = 0;
  while (i + 1 < nums.length) {
    const questionId = nums[i++];
    const count = nums[i++];
    const ints = nums.slice(i, i + count);
    i += count;
    if (ints.length < count) break; // malformed tail

    const question = byId.get(questionId);
    if (!question) continue;
    answers[String(questionId)] = intsToAnswer(question, ints);
  }

  return answers;
}

/** Reads the answers a `/forms` page was opened with, from its `?a=` token. */
export function decodeAnswersFromParams(
  params: Pick<URLSearchParams, 'get'>,
  availableQuestions: QuestionDataInput[] = []
): Answers {
  const token = params.get(QUERY_KEY);
  return token ? decodeAnswers(token, availableQuestions) : {};
}

/** Builds the `?a=` query string for a redirect to the full form, or '' when nothing to carry. */
export function buildAnswersQuery(answers: Answers): string {
  const token = encodeAnswers(answers);
  return token ? `?${QUERY_KEY}=${token}` : '';
}
